import { Box, ModalDialog, Spacer, Text } from '@jfs/ui-new'
import { useIsLargeScreen } from '@jfs/ui-new/src/utils/utils'
import { getLoadingState } from '@jfs/utils'
import { useIsFocused, useNavigation } from '@react-navigation/native'
import { FC, useCallback, useEffect, useState } from 'react'
import EmptyView from '../../../comps/EmptyView'
import { ModBox } from '../../../comps/ModifiedBox/ModifiedBox'
import { NewTable } from '../../../comps/NewTable'
import Tabbar, { TabbarType } from '../../../comps/Tabbar'
import {
  recentsWebTabData,
  refundsRecentsTitleDataV2,
  settlementRecentsTitleDataV2,
  transactionRecentsTitleDataV2
} from '../../../data/static/data'
import { dashboardStrings, staticStrings, transactionScreenStrings } from '../../../data/static/strings'
import { useLazyApiData, useStores } from '../../../data/store'
import { Routes } from '../../../nav/routes'
import TransactionDetails from '../../../screens/TransactionDetails'
import { getCustomColumnWidth, isMobile, useWindowDimensions } from '../../../util/util'
import { styles } from './styles'

export interface RecentPaymentsProps {
  title?: string
  toggleLoader: (data: boolean) => void
}

const routeList = new Map([
  [0, Routes.TRANSACTIONS],
  [1, Routes.SETTLEMENTS],
  [2, Routes.REFUNDS],
])

const RecentPayments: FC<RecentPaymentsProps> = ({ toggleLoader }: RecentPaymentsProps) => {
  const navigation = useNavigation()
  const {
    getTrxn,
    mid,
    fetchRecentTranasctions,
    fetchRecentSettlements,
    fetchSettlementSummary,
    getRefunds,
    updateSelectedTxn,
    fetchRecentTransactionsAction,
    isFieldsNotEmpty,
    getTransactionsColumn,
    selectedFromDate,
    selectedToDate,
    resetFilters,
    isTransactionTabEnabled,
    isSettlementEnabled,
    isRefundedTabVisible,
  } = useStores((store) => ({
    selectedFromDate: store.settings.metaData.selectedDate.fromDate,
    selectedToDate: store.settings.metaData.selectedDate.toDate,
    updateSelectedTxn: store.transactions.updateSelectedTxn,
    getTrxn: store.transactions.getTransactionsV3,
    getRefunds: store.transactions.getRefundsTxnsV3,
    mid: store.user.selectedMid,
    fetchSettlementSummary: store.settlements.fetchSettlementSummary,
    fetchRecentTranasctions: store.transactions.fetchRecentTranasctions,
    fetchRecentSettlements: store.settlements.fetchSettlementDataRecents,
    fetchRecentTransactionsAction: store.fetchRecentTransactionsAction,
    isFieldsNotEmpty: store.transactions.isFieldsNotEmpty,
    getTransactionsColumn: store.transactions.getTransactionsColumnV3,
    resetFilters: store.transactions.resetFilters,
    isTransactionTabEnabled: store.userManagement.isRecentTransactionTabEnabled(),
    isSettlementEnabled: store.userManagement.isRecentSettlementTabEnabled(),
    isRefundedTabVisible: store.userManagement.isRecentRefundReportTabEnabled(),
    selectedDate: store.settings.metaData.selectedDate,
  }))

  const viewPortWidth = useWindowDimensions()

  const isFocused = useIsFocused()

  const themedStyle = styles()
  const [value, setValue] = useState(0)
  const [error, setError] = useState('' || false)
  const [showTransactionDetails, setTransactionDetails] = useState(false)
  const [trxId, setTrxId] = useState('')
  const [refundId, setRefundId] = useState('')
  const [transactionDate, setTransactionDate] = useState('')
  const [fetchTxn, fetchTxnHandler] = useLazyApiData(getTrxn)
  const [fetchSettlements, fetchStmtHandler] = useLazyApiData(fetchSettlementSummary)
  const [fetchRefunds, fetchRefundsHandler] = useLazyApiData(getRefunds)
  const [fetchTxnColumns, txnColumnHandler] = useLazyApiData(getTransactionsColumn)

  useEffect(() => {
    resetFilters()
  }, [resetFilters])

  useEffect(() => {
    if (mid && !isFieldsNotEmpty) {
      fetchTxnColumns()
    }
  }, [mid, isFieldsNotEmpty, fetchTxnColumns])

  const isLargeScreen = useIsLargeScreen()

  const isSmallDevice = isMobile(isLargeScreen)

  const apis = [fetchTxnHandler, fetchStmtHandler, fetchRefundsHandler, txnColumnHandler]

  const fetchRecentTransactions = () => {
    if (isFocused) {
      fetchRecentTransactionsAction(value, fetchTxn, fetchSettlements, fetchRefunds, true, true)
    }
  }

  const fetchTransactionCallback = useCallback(fetchRecentTransactions, [
    fetchTxn,
    value,
    mid,
    fetchSettlements,
    fetchRefunds,
  ])

  useEffect(() => {
    fetchTransactionCallback()
  }, [fetchTransactionCallback, selectedFromDate, selectedToDate])

  const getListData = (): any[] => {
    if (value === 1) {
      return fetchRecentSettlements(isSmallDevice)
    }
    return fetchRecentTranasctions(routeList.get(value), isSmallDevice)
  }

  const getHeaderTitle = () => {
    if (value === 1) {
      return settlementRecentsTitleDataV2
    }
    return routeList.get(value) === Routes.TRANSACTIONS ? transactionRecentsTitleDataV2 : refundsRecentsTitleDataV2
  }
  const ListContent = getListData()

  const handleRetry = (): void => {
    fetchRecentTransactions()
  }

  const getError = (apiHandlers: any, tabIndex: number): boolean => {
    return apiHandlers[tabIndex]?.data?.message ?? false
  }
  const loadingState = getLoadingState(apis)

  useEffect(() => {
    if (loadingState === true) {
      toggleLoader(true)
    } else {
      toggleLoader(false)
    }
  }, [loadingState, toggleLoader])

  useEffect(() => {
    setError(getError(apis, value))
  }, [txnColumnHandler, fetchTxnHandler, fetchStmtHandler, fetchRefundsHandler, apis, value])
  const getEmptyMessage = (): string => {
    return ListContent.length === 0 ? transactionScreenStrings.noRecords : transactionScreenStrings.empty
  }
  const closeTrxDetailsView = (): void => {
    setTransactionDetails(false)
    setTrxId('')
    setRefundId('')
    setTransactionDate('')
  }

  const onTrxSelection = (item: any): void => {
    if (item.id && value !== 1) {
      setTrxId(item.id)
      value === 2 && setRefundId(item?.transactionId)
      setTransactionDate(item.date)
      setTransactionDetails(true)
    }
  }
  const getScrollViewStyle = (): any => {
    if (ListContent.length > 0) {
      return themedStyle.tableContainer
    }
    return themedStyle.emptyContainer
  }
  const scrollViewStyle = getScrollViewStyle()

  /*   const renderHoverView = (data: any, transactionType: number): React.ReactElement => {
    return (
      <ModBox show={false}>
        <TransactionHoverView data={data} transactionType={transactionType} />
      </ModBox>
    )
  } */

  const handleTransactionAction = (data: ListData): void => {
    if (data) {
      updateSelectedTxn(data.id)
      navigation.navigate(Routes.HOME_INITIATEREFUND)
      setTransactionDetails(false)
    }
  }

  const initiateRefund = (id: string) => {
    const filtereData = getListData().filter((item) => item.id === id)
    const data = filtereData[0]
    handleTransactionAction(data)
  }

  // TODO: Try not to have List inside scrollView. Remove scrollview
  const scrollViewLoadingState = loadingState || ListContent.length < 1

  const updateValue = (tabValue) => {
    setValue(tabValue)
    setError(false)
  }

  const showRecentTab = isTransactionTabEnabled || isSettlementEnabled || isRefundedTabVisible

  const sowEmptyView = (ListContent.length === 0 || error || getEmptyMessage()) && !loadingState
  return (
    <Box {...themedStyle.containerWeb}>
      <ModalDialog
        children={
          <TransactionDetails
            transactionId={trxId}
            visible={showTransactionDetails}
            closeTrxDetailsView={closeTrxDetailsView}
            initiateRefund={initiateRefund}
            path={routeList.get(value)}
            transactionDate={transactionDate}
            refundId={refundId}
          />
        }
        type={'Right'}
        show={showTransactionDetails}
        onDismiss={closeTrxDetailsView}
      />
      <ModBox show={showRecentTab} {...themedStyle.stickyHeader}>
        <Text testID={'pageTitle'} type={'h0Bold'}>
          {staticStrings.recents}
        </Text>
        <Box {...themedStyle.tabbarweb} height={55}>
          <Tabbar
            default={value}
            titles={recentsWebTabData(isTransactionTabEnabled, isSettlementEnabled, isRefundedTabVisible)}
            onSelected={updateValue}
            type={TabbarType.recentPayments}
          />
        </Box>
      </ModBox>
      <Spacer hide={!showRecentTab} size={4} />
      <EmptyView
        testID={'recent-txn-view'}
        onRetry={handleRetry}
        isLoading={false}
        error={error}
        emptyMessage={getEmptyMessage()}
        loadingMessage={dashboardStrings.loading}
        hide={!sowEmptyView || !showRecentTab}
        loaderColor={'BLUE'}
      />
      {!getEmptyMessage() && !error && showRecentTab && (
        <>
          <Box {...scrollViewStyle} nativeID={'scroll-horizontal-Table'} f={1} minHeight={250}>
            <NewTable
              data={ListContent}
              rowHeight={48}
              headerHeight={64}
              customWidthFunc={getCustomColumnWidth}
              onRowClick={onTrxSelection}
              tableWidth={viewPortWidth > 1200 ? viewPortWidth - 300 : viewPortWidth}
              headerRowKeys={getHeaderTitle()}
            />
          </Box>
          <Spacer size={8} />
          <ModBox show={!scrollViewLoadingState}>
            <Text type={'h5Light'}>* latest 10 records</Text>
          </ModBox>
        </>
      )}
    </Box>
  )
}

export default RecentPayments
